import {
  Alert,
  Box,
  Button as MuiButton,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { ButtonMenu, ColorOption } from "../../components/Menu/ButtonMenu";
import { MenuItem } from "../../components/Menu/MenuItem";
import { ExpandMore, Edit, KeyboardArrowLeft } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import { PagePaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { FeedGrid } from "./components/FeedGrid";
import {
  CHUNK_SIZE,
  useChunkedBookDetails,
} from "./hooks/useChunkedBookDetails";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import {
  Link as RouterLink,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useAcquireBookStatus } from "./hooks/useAcquireBookStatus";
import { useSamboKunFeedGridIntegration } from "./hooks/useSamboKunFeedGridIntegration";
import { convertRows } from "./utils/convertRows";
import { SendToSamboKunDialog } from "@/features/partner/components/SendToSamboKunDialog";
import { type AcquireBookStatus } from "@/features/acquireBookStatus/types";
import { useApiClient } from "@/hooks/useApiClient";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { type HandOffMessage } from "@/types/handOffMessage";
import { useFeatureFlags } from "@/configs/featureFlag";
import Tooltip from "@mui/material/Tooltip";
import { HttpStatusCode, isAxiosError } from "axios";
import { acquireBookIsDone } from "@/utils/utilsBookStatus";
import { getPrefectureIdByValue } from "@/utils/utilsAcquireBook";
import { AcquireBookModal } from "./components/AcquireBookModal";
import { type ISingleBookAcquireDialogData } from "../../types/singleBookAcquireDialog";
import { AcquireBookEditModal } from "@/features/acquireBookStatus/components/AcquireBookEditModal";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";
import {
  type BookTypeEng,
  type AcquireMultipleBookHandoffData,
  type AllMattersAnalyzeOptions,
} from "@/types/acquirebook";
import { ConfirmAnalyzeDialog } from "./components/ConfirmAnalyzeDialog";
import { ButtonVariantOption } from "@/components/Button";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";
import { useSalesforceTransferGate } from "@/features/salesforceIntegration/hooks/useSalesforceTransferGate";
import { useSalesforceFeedGridIntegration } from "@/features/salesforceIntegration/hooks/useSalesforceFeedGridIntegration";
import { SalesforceTransferConfirmDialog } from "@/features/salesforceIntegration/components/SalesforceTransferConfirmDialog";

import { userDataContext } from "@/hooks/useUserData";
import { getListReturnPath } from "@/features/acquireBookList/utils/searchState";

export const AcquireBookStatusPresenter: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const { data: user } = useContext(userDataContext);
  const listReturnPath = getListReturnPath(location.state, user.id);

  // 大量明細の依頼（DataLens 一括依頼や、バッチ分割で1,000件超えを許容した通常依頼）は
  // 全件取得ではタイムアウトするため、まず1件だけ取得して依頼の種別・明細数を判定し、
  // 該当時のみ分割取得へ切り替える
  const [fetchMode, setFetchMode] = useState<"probe" | "full" | "chunked">(
    "probe"
  );
  const {
    data,
    error: statusError,
    getStatusString,
    getRemainingCountWithErrorCount,
    mutate,
  } = useAcquireBookStatus(
    id,
    fetchMode === "full" ? null : { page: 0, pageSize: 1 }
  );
  useEffect(() => {
    if (fetchMode === "probe" && data !== undefined) {
      // NOTE: 境界は「>」で正しい。ちょうど CHUNK_SIZE 件は分割取得でも1チャンク
      // （limit=CHUNK_SIZE の1リクエスト）になり、全件取得と負荷が同一のため
      setFetchMode(
        data.isDatalensRequest || data.count > CHUNK_SIZE ? "chunked" : "full"
      );
    }
  }, [fetchMode, data]);

  const prevIdRef = useRef(id);
  useEffect(() => {
    if (prevIdRef.current !== id) {
      prevIdRef.current = id;
      setFetchMode("probe");
    }
  }, [id]);

  // NOTE: count は受付時に明細が全件作成されて以降変わらないため、判定時点の値を
  // 凍結せずポーリング結果をそのまま渡してよい
  const chunkedDetails = useChunkedBookDetails(
    id,
    fetchMode === "chunked",
    data?.count
  );
  // 取得完了（残数が0になった）時点で明細を読み込み直し、最終ステータスを行に反映する
  const prevRemainingRef = useRef<number | null>(null);
  const [remainingCount] = getRemainingCountWithErrorCount(data);
  useEffect(() => {
    if (
      fetchMode === "chunked" &&
      remainingCount === 0 &&
      (prevRemainingRef.current ?? 0) > 0
    ) {
      chunkedDetails.reload();
    }
    prevRemainingRef.current = remainingCount;
  }, [fetchMode, remainingCount, chunkedDetails.reload]);
  const navigate = useNavigate();
  const { apiClient } = useApiClient();

  // probe 中の1件だけのlistは一覧として描画しない。
  // DataLens 依頼は分割取得で組み立てた全件リストを使う
  const acquireBookDetails: AcquireBookStatus[] = useMemo(
    () =>
      fetchMode === "full"
        ? data?.list ?? []
        : fetchMode === "chunked"
        ? chunkedDetails.list ?? []
        : [],
    [fetchMode, data?.list, chunkedDetails.list]
  );
  const [selectedOwnerInfos, setSelectedOwnerInfos] = useState<
    Map<number, boolean>
  >(
    acquireBookDetails.reduce<Map<number, boolean>>((a, x) => {
      a.set(x.id, false);
      return a;
    }, new Map<number, boolean>())
  );
  const [selectedAllMattersInfos, setSelectedAllMattersInfos] = useState<
    Map<number, boolean>
  >(
    acquireBookDetails.reduce<Map<number, boolean>>((a, x) => {
      a.set(x.id, false);
      return a;
    }, new Map<number, boolean>())
  );

  const [singleBookAcquireDialogData, setSingleBookAcquireDialogData] =
    useState<ISingleBookAcquireDialogData>({
      prefecturesId: 0,
      prefectures: "",
      address: "",
      chiban: "",
      kaokuNumber: "",
      realEstateTypeEng: "",
    });

  const [openOwnerAnalyzeDialog, setOpenOwnerAnalyzeDialog] =
    useState<boolean>(false);
  const [openAllMattersAnalyzeDialog, setOpenAllMattersAnalyzeDialog] =
    useState<boolean>(false);
  const isReadOnly = useReadOnlyUser();

  const handleOwnerAnalyzeButtonClick = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): void => {
    if (isReadOnly) {
      cancelLoading();
      toast.error(READ_ONLY_TOOLTIP_MESSAGE);
      return;
    }

    const detailIds: number[] = [];
    for (const [detailId, checked] of selectedOwnerInfos) {
      if (checked) {
        detailIds.push(detailId);
      }
    }

    // ボタンのdisabled判定（hasOwnerAnalyzeTargets、レンダリング時点）とは別に、
    // ハンドラ単体で呼ばれた場合にも空配列でAPIを呼ばないよう送信直前にも防御する。
    if (detailIds.length === 0) {
      cancelLoading();
      return;
    }

    const body = {
      detailIds,
    };
    apiClient.post("/owner-analyze/picture-request", body).catch((e) => {
      Sentry.captureException(e);
      cancelLoading();
      if (isAxiosError(e) && e.response?.status === HttpStatusCode.Forbidden) {
        toast.error(READ_ONLY_TOOLTIP_MESSAGE);
        return;
      }
      toast.error("所有者事項解析に失敗しました");
    });

    // 上のapiClient.post()はawaitしてないしthen()も無いため、成功しても失敗してもすぐに下のコードが実行される

    const handOffMessage: HandOffMessage = {
      command: "OWNER_INFO_SHOW_ACCEPT_MESSAGE",
    };

    // 成功した場合は次の画面へ遷移するのでcancelLoading()の呼び出しは不要
    navigate("/ownerinfo/list", {
      replace: true,
      state: { handOffMessage },
    });
  };

  const handleAllMattersAnalyzeButtonClick = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void,
    analysisOptions?: AllMattersAnalyzeOptions
  ): void => {
    if (isReadOnly) {
      cancelLoading();
      toast.error(READ_ONLY_TOOLTIP_MESSAGE);
      return;
    }

    const detailIds: number[] = [];
    for (const [detailId, checked] of selectedAllMattersInfos) {
      if (checked) {
        detailIds.push(detailId);
      }
    }

    // ボタンのdisabled判定（hasAllMattersAnalyzeTargets、レンダリング時点）とは別に、
    // ハンドラ単体で呼ばれた場合にも空配列でAPIを呼ばないよう送信直前にも防御する。
    if (detailIds.length === 0) {
      cancelLoading();
      return;
    }

    const body = {
      detailIds,
      ...(analysisOptions ?? {}),
    };
    apiClient.post("/all-matters-analyze/picture-request", body).catch((e) => {
      Sentry.captureException(e);
      cancelLoading();
      if (isAxiosError(e) && e.response?.status === HttpStatusCode.Forbidden) {
        toast.error(READ_ONLY_TOOLTIP_MESSAGE);
        return;
      }
      toast.error("全部事項解析に失敗しました");
    });

    // 上のapiClient.post()はawaitしてないしthen()も無いため、成功しても失敗してもすぐに下のコードが実行される
    const handOffMessage: HandOffMessage = {
      command: "ALL_MATTERS_SHOW_ACCEPT_MESSAGE",
    };

    // 成功した場合は次の画面へ遷移するのでcancelLoading()の呼び出しは不要
    navigate("/allmatters/list", {
      replace: true,
      state: { handOffMessage },
    });
  };

  const acStatusMessage = getStatusString(data);
  const shouldShowTooltip = (): boolean => {
    // 処理が完了しているかつzipFileUriが存在しない場合はtrue
    if (acStatusMessage === "") {
      return false;
    }
    return acquireBookIsDone(acStatusMessage) && !data?.zipFileUri;
  };

  const { ownerInfoViaScraping, displayCheckboxAllMattersAnalyze } =
    useFeatureFlags();
  const hasOwnerAnalyzeTargets = [...selectedOwnerInfos.values()].some(
    (value) => value
  );
  const hasAllMattersAnalyzeTargets = [
    ...selectedAllMattersInfos.values(),
  ].some((value) => value);
  const [acquireBookOpen, setAcquireBookOpen] = useState<boolean>(false);
  const [openAcquireBookNameDialog, setOpenAcquireBookNameDialog] =
    useState<boolean>(false);
  const [selectedRowIds, setSelectedRowIds] = useState<GridSelectionModel>([]);
  const identificationNameDisabledMessage =
    "他ユーザーが設定した識別名は編集できません";
  const otherUserAnalyzeDisabledMessage =
    "他ユーザーが取得した登記は解析できません";

  // Salesforce連携の有効・無効。機能フラグと契約の判定はここだけで行い、
  // 行変換や下位 hook へは真偽値として渡す（共通処理をLaunchDarklyに依存させない）。
  const { showSalesforceTransfer } = useSalesforceTransferGate();

  // DataGrid 表示用に変換した行データ。FeedGrid と参謀くん連携 hook の双方で
  // 共有するため、レンダリングルート（presenter）で計算してメモ化する。
  // Salesforce連携が無効なら物件の識別子は集めない。フラグの切り替えで
  // 古い変換結果が残らないよう、依存にも含める。
  const convertedRows = useMemo(
    () =>
      convertRows(acquireBookDetails, {
        collectsUniqueIds: showSalesforceTransfer,
      }),
    [acquireBookDetails, showSalesforceTransfer]
  );

  // 参謀くん連携 UI まわりの状態と派生値を hook に集約。
  // 「操作メニュー」内の MenuItem 描画と SendToSamboKunDialog の双方で
  // 参照するため、presenter レベルで保持する。
  const {
    showSendToSamboKun,
    samboKunDialogTarget,
    openSamboKunDialog,
    closeSamboKunDialog,
    excludedPictureRequestDetailIds,
    isSamboKunOwnerDisabled,
    isSamboKunAllMattersDisabled,
    getSamboKunTooltipText,
  } = useSamboKunFeedGridIntegration({
    convertedRows,
    selectedRowIds,
    bindOwnerAnalyzeRequestId: data?.bindOwnerAnalyzeRequestId,
    bindAllMattersAnalyzeRequestId: data?.bindAllMattersAnalyzeRequestId,
  });

  // Salesforce連携（機能5）。FeedGridの選択と連携操作のつなぎこみは hook へ集約し、
  // 判定条件が画面のあちこちに散らないようにする（実装計画書7.4節）。
  const pictureRequestId = id ? Number(id) : undefined;
  const {
    realEstateByUniqueId: salesforceRealEstateByUniqueId,
    inFlightUniqueIds: salesforceInFlightUniqueIds,
    actionState: salesforceActionState,
    summary: salesforceSummary,
    isConfirmDialogOpen: isSalesforceConfirmOpen,
    openConfirmDialog: openSalesforceConfirm,
    closeConfirmDialog: closeSalesforceConfirm,
    confirmTransfer: confirmSalesforceTransfer,
    transferStatus: salesforceTransferStatus,
    transferErrorMessage: salesforceTransferErrorMessage,
    hasEligibilityRefreshFailed: hasSalesforceRefreshFailed,
    retryEligibilityRefresh: retrySalesforceRefresh,
  } = useSalesforceFeedGridIntegration({
    isEnabled: showSalesforceTransfer,
    pictureRequestId,
    convertedRows,
    selectedRowIds,
    isReadOnly,
  });

  // 登記/図面取得ボタンが押された時の処理
  const handleAcquireMultipleBooksClick = (): void => {
    if (!acquireBookDetails.length || !selectedRowIds.length) return;

    const multipleBookAcquireData: AcquireMultipleBookHandoffData[] =
      acquireBookDetails
        .filter((row) => selectedRowIds.includes(row.id))
        .map((row) => ({
          id: row.id,
          bookType: row.realEstateType as BookTypeEng,
          prefCode: String(getPrefectureIdByValue(row.prefectureName) ?? 0),
          prefName: row.prefectureName,
          locationFull: row.location,
          chibanKaokuNumber: row.chibanKaokuNumber,
          kyoutan: false,
          sintaku: false,
          genzai: false,
          ownerInfo: false,
          electricDrawings: false,
          tisekiDrawings: false,
          tiekiDrawings: false,
          buildingDrawings: false,
        }));

    if (multipleBookAcquireData.length) {
      // feedのテーブルが1件以上選択されている場合複数登記取得画面へ遷移
      navigate("/acquiremultiplebooks", {
        state: multipleBookAcquireData,
      });
    } else {
      // feedのテーブルが選択されていない場合単体登記取得画面へ遷移
      navigate("/acquirebook", { state: {} });
    }
  };

  // 識別名を更新後、すぐにデータの再フェッチを行う
  const handleRefreshAfterEdit = (): void => {
    mutate(data).catch(() => {
      toast.error("データの再取得に失敗しました。");
    });
  };

  return (
    <PagePaper>
      <form>
        <Box sx={{ display: "flex" }}>
          {/* ページタイトル */}
          <Box sx={{ display: "flex", mb: 3 }}>
            <RequestQuoteIcon sx={{ mr: 1 }} fontSize="large" />
            <PageTitle>取得済不動産登記の詳細を確認する</PageTitle>
          </Box>
        </Box>
        <Grid
          container
          spacing={2}
          sx={{ mb: 2 }}
          justifyContent={"space-between"}
          alignItems={"flex-end"}
        >
          <Grid item xs={6} sx={{ ml: 0 }}>
            <Typography
              sx={{ color: "black", fontSize: "16px", fontWeight: "bold" }}
            >
              識別名: {data?.name}
            </Typography>
            <Typography
              fontWeight={"bold"}
              sx={{ color: "#4A86DF", fontSize: "16px" }}
            >
              ステータス: {acStatusMessage}
            </Typography>
          </Grid>
        </Grid>
        <Grid container sx={{ mb: 2, mt: 3 }}>
          <Grid item xs={12}>
            <Stack
              direction="row"
              sx={{ alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}
            >
              <Tooltip
                title={
                  data?.isOtherUserResource
                    ? identificationNameDisabledMessage
                    : ""
                }
                placement="top"
                arrow
                disableHoverListener={!data?.isOtherUserResource}
              >
                <span>
                  <MuiButton
                    variant="outlined"
                    color="brand"
                    onClick={() => {
                      setOpenAcquireBookNameDialog(true);
                    }}
                    startIcon={<Edit />}
                    disabled={data?.isOtherUserResource}
                    sx={{
                      borderRadius: "3px",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    識別名編集
                  </MuiButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  shouldShowTooltip() ? (
                    <>
                      全て失敗している場合はファイルは作成されません。
                      <br />
                      取得されて間もない一部の登記が含まれる場合は、一定期間ダウンロードできないことがあります。
                      <br />
                      それ以外の場合はファイルを作成中です。数分間かかる場合があります。
                    </>
                  ) : (
                    <></>
                  )
                }
                placement="top"
                arrow
                disableHoverListener={!shouldShowTooltip()}
              >
                <span>
                  <MuiButton
                    variant="outlined"
                    color="brand"
                    component="a"
                    disabled={!data?.zipFileUri}
                    startIcon={<DownloadIcon />}
                    sx={{
                      borderRadius: "3px",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                    {...(data?.zipFileUri
                      ? {
                          href: data.zipFileUri,
                          target: "_blank",
                          rel: "noopener noreferrer",
                        }
                      : {})}
                  >
                    一括ダウンロード
                  </MuiButton>
                </span>
              </Tooltip>
              <ButtonMenu
                label="操作メニュー"
                variant={ButtonVariantOption.Outlined}
                buttonColor={ColorOption.Brand}
                endIcon={<ExpandMore />}
                sx={{
                  borderRadius: "3px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                <MenuItem
                  onClick={handleAcquireMultipleBooksClick}
                  disabled={!selectedRowIds.length}
                  sx={{ py: 1 }}
                >
                  選択した登記の再取得に進む
                </MenuItem>
                {ownerInfoViaScraping &&
                  data?.bindOwnerAnalyzeRequestId === null && (
                    <Tooltip
                      title={
                        isReadOnly
                          ? READ_ONLY_TOOLTIP_MESSAGE
                          : data?.isOtherUserResource
                          ? otherUserAnalyzeDisabledMessage
                          : ""
                      }
                      placement="left"
                      arrow
                      disableHoverListener={
                        !isReadOnly && !data?.isOtherUserResource
                      }
                    >
                      <span>
                        <MenuItem
                          onClick={() => {
                            setOpenOwnerAnalyzeDialog(true);
                          }}
                          disabled={
                            isReadOnly ||
                            data?.isOtherUserResource ||
                            !hasOwnerAnalyzeTargets
                          }
                          sx={{ py: 1 }}
                        >
                          所有者事項を解析する
                        </MenuItem>
                      </span>
                    </Tooltip>
                  )}
                {ownerInfoViaScraping && data?.bindOwnerAnalyzeRequestId && (
                  <MenuItem
                    component={RouterLink}
                    sx={{ py: 1 }}
                    {...{
                      to: `/ownerinfo/detail/${data?.bindOwnerAnalyzeRequestId}`,
                    }}
                  >
                    解析済みの所有者事項を確認する
                  </MenuItem>
                )}
                {showSendToSamboKun &&
                  data?.bindOwnerAnalyzeRequestId != null &&
                  (() => {
                    const tooltipText = getSamboKunTooltipText("owner");
                    return (
                      <Tooltip
                        title={tooltipText}
                        placement="left"
                        arrow
                        // limit-loading は空文字を返すため、空タイトル時は
                        // 空 Tooltip 描画を抑止する
                        disableHoverListener={
                          !isSamboKunOwnerDisabled || tooltipText === ""
                        }
                      >
                        <span>
                          <MenuItem
                            disabled={isSamboKunOwnerDisabled}
                            onClick={() => {
                              openSamboKunDialog("owner");
                            }}
                            sx={{ py: 1 }}
                          >
                            解析済みの所有者事項を参謀くんへ連携する
                          </MenuItem>
                        </span>
                      </Tooltip>
                    );
                  })()}
                {displayCheckboxAllMattersAnalyze &&
                  data?.bindAllMattersAnalyzeRequestId === null && (
                    <Tooltip
                      title={
                        isReadOnly
                          ? READ_ONLY_TOOLTIP_MESSAGE
                          : data?.isOtherUserResource
                          ? otherUserAnalyzeDisabledMessage
                          : ""
                      }
                      placement="left"
                      arrow
                      disableHoverListener={
                        !isReadOnly && !data?.isOtherUserResource
                      }
                    >
                      <span>
                        <MenuItem
                          onClick={() => {
                            setOpenAllMattersAnalyzeDialog(true);
                          }}
                          disabled={
                            isReadOnly ||
                            data?.isOtherUserResource ||
                            !hasAllMattersAnalyzeTargets
                          }
                          sx={{ py: 1 }}
                        >
                          全部事項を解析する
                        </MenuItem>
                      </span>
                    </Tooltip>
                  )}
                {displayCheckboxAllMattersAnalyze &&
                  data?.bindAllMattersAnalyzeRequestId && (
                    <MenuItem
                      component={RouterLink}
                      sx={{ py: 1 }}
                      {...{
                        to: `/allmatters/detail/${data?.bindAllMattersAnalyzeRequestId}`,
                      }}
                    >
                      解析済みの全部事項を確認する
                    </MenuItem>
                  )}
                {showSendToSamboKun &&
                  data?.bindAllMattersAnalyzeRequestId != null &&
                  (() => {
                    const tooltipText = getSamboKunTooltipText("allMatters");
                    return (
                      <Tooltip
                        title={tooltipText}
                        placement="left"
                        arrow
                        disableHoverListener={
                          !isSamboKunAllMattersDisabled || tooltipText === ""
                        }
                      >
                        <span>
                          <MenuItem
                            disabled={isSamboKunAllMattersDisabled}
                            onClick={() => {
                              openSamboKunDialog("allMatters");
                            }}
                            sx={{ py: 1 }}
                          >
                            解析済みの全部事項を参謀くんへ連携する
                          </MenuItem>
                        </span>
                      </Tooltip>
                    );
                  })()}
                {showSalesforceTransfer && (
                  <Tooltip
                    title={salesforceActionState.tooltipMessage}
                    placement="left"
                    arrow
                    disableHoverListener={
                      salesforceActionState.tooltipMessage === ""
                    }
                  >
                    <span>
                      <MenuItem
                        disabled={salesforceActionState.isDisabled}
                        onClick={openSalesforceConfirm}
                        sx={{ py: 1 }}
                      >
                        選択した登記をSalesforceへ連携する
                      </MenuItem>
                    </span>
                  </Tooltip>
                )}
              </ButtonMenu>
            </Stack>
          </Grid>
        </Grid>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* 依頼情報そのものが取得できていない（判定用の取得やその再試行が失敗し続けている）場合。
              画面が空白のまま止まって見えないよう明示する。30秒ポーリングで復旧すれば自動的に消える */}
          {data === undefined && statusError != null && (
            <Alert severity="error">
              依頼情報の取得に失敗しました。時間をおいてページを再読み込みしてください
            </Alert>
          )}
          {/* 連携ジョブの失敗は物件単位に残らないため、行ではなくここで知らせる */}
          {showSalesforceTransfer && salesforceTransferStatus === "FAILED" && (
            <Alert severity="error">
              {salesforceTransferErrorMessage ??
                "直近のSalesforceへの連携に失敗しました"}
            </Alert>
          )}
          {/* 取り直しに失敗すると操作を止めたままになるため、復帰手段をその場に置く */}
          {showSalesforceTransfer && hasSalesforceRefreshFailed && (
            <Alert
              severity="warning"
              action={
                <MuiButton
                  color="inherit"
                  size="small"
                  onClick={retrySalesforceRefresh}
                >
                  再確認する
                </MuiButton>
              }
            >
              連携結果を確認できませんでした。再確認するまでSalesforceへの連携はできません。
            </Alert>
          )}
          {fetchMode === "chunked" &&
            (chunkedDetails.error != null || chunkedDetails.list == null) && (
              <Alert severity={chunkedDetails.error ? "error" : "info"}>
                {chunkedDetails.error
                  ? "明細の読み込みに失敗しました。ページを再読み込みしてください"
                  : `明細を読み込み中… ${chunkedDetails.loadedCount.toLocaleString()} / ${(
                      data?.count ?? 0
                    ).toLocaleString()}件`}
              </Alert>
            )}
          <FeedGrid
            convertedRows={convertedRows}
            selectedOwnerInfos={selectedOwnerInfos}
            setSelectedOwnerInfos={setSelectedOwnerInfos}
            bindOwnerAnalyzeRequestId={data?.bindOwnerAnalyzeRequestId}
            selectedAllMattersInfos={selectedAllMattersInfos}
            setSelectedAllMattersInfos={setSelectedAllMattersInfos}
            bindAllMattersAnalyzeRequestId={
              data?.bindAllMattersAnalyzeRequestId
            }
            setAcquireBookOpen={setAcquireBookOpen}
            setSingleBookAcquireDialogData={setSingleBookAcquireDialogData}
            selectedRowIds={selectedRowIds}
            setSelectedRowIds={setSelectedRowIds}
            identificationName={data?.name}
            isDatalensRequest={data?.isDatalensRequest}
            showSalesforceTransfer={showSalesforceTransfer}
            salesforceRealEstateByUniqueId={salesforceRealEstateByUniqueId}
            salesforceInFlightUniqueIds={salesforceInFlightUniqueIds}
          />
        </Box>
        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            component={RouterLink}
            to={listReturnPath}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>一覧に戻る</Typography>
            </Box>
          </Link>
        </Box>
      </form>
      <AcquireBookModal
        acquireBookOpen={acquireBookOpen}
        handleClose={() => {
          setAcquireBookOpen(false);
        }}
        row={singleBookAcquireDialogData}
      />
      {openAcquireBookNameDialog && !data?.isOtherUserResource && (
        <AcquireBookEditModal
          open={openAcquireBookNameDialog}
          setOpen={setOpenAcquireBookNameDialog}
          handleClose={handleRefreshAfterEdit}
          pictureRequestId={Number(id)}
          data={data}
        />
      )}
      <ConfirmAnalyzeDialog
        open={openOwnerAnalyzeDialog}
        handleClose={() => {
          setOpenOwnerAnalyzeDialog(false);
        }}
        handleSubmit={handleOwnerAnalyzeButtonClick}
        type="owner"
        isReadOnlyBlocked={isReadOnly}
      />
      <ConfirmAnalyzeDialog
        open={openAllMattersAnalyzeDialog}
        handleClose={() => {
          setOpenAllMattersAnalyzeDialog(false);
        }}
        handleSubmit={handleAllMattersAnalyzeButtonClick}
        type="allMatters"
        isReadOnlyBlocked={isReadOnly}
      />
      {/* 参謀くん連携確認ダイアログ */}
      {showSendToSamboKun &&
        samboKunDialogTarget === "owner" &&
        data?.bindOwnerAnalyzeRequestId != null && (
          <SendToSamboKunDialog
            open={samboKunDialogTarget === "owner"}
            onClose={closeSamboKunDialog}
            target="owner"
            analyzeRequestId={data.bindOwnerAnalyzeRequestId}
            excludedPictureRequestDetailIds={excludedPictureRequestDetailIds}
          />
        )}
      {showSendToSamboKun &&
        samboKunDialogTarget === "allMatters" &&
        data?.bindAllMattersAnalyzeRequestId != null && (
          <SendToSamboKunDialog
            open={samboKunDialogTarget === "allMatters"}
            onClose={closeSamboKunDialog}
            target="allMatters"
            analyzeRequestId={data.bindAllMattersAnalyzeRequestId}
            excludedPictureRequestDetailIds={excludedPictureRequestDetailIds}
          />
        )}
      {/* Salesforce連携確認ダイアログ */}
      {showSalesforceTransfer && (
        <SalesforceTransferConfirmDialog
          open={isSalesforceConfirmOpen}
          onClose={closeSalesforceConfirm}
          onConfirm={confirmSalesforceTransfer}
          summary={salesforceSummary}
          actionState={salesforceActionState}
        />
      )}
    </PagePaper>
  );
};
